/**
 * SWISS programme tiers and the dashboard modules each one unlocks.
 * ==================================================================
 * Every school is assigned a tier. The portal shows one dashboard for
 * all schools and grays out the modules the school's tier doesn't
 * include, with a prompt to contact My Safe Spaces to upgrade.
 *
 * 6 tiers. SWISS Bridge is a 12-month counsellor-capacity "on-ramp"
 * programme — preparation for SWISS certification, not certification
 * itself — so its module set is NOT a subset of Lite's. SWISS On-Demand
 * is a tier-less posture: the school hasn't bought a fixed package and
 * pays for individual modules (workshops, sessions, etc.) as
 * needed — each one enabled via the per-school addOnModules[] array.
 * Tiers are therefore defined with explicit module sets rather than a
 * strict cumulative model. Each tier also carries a colour, used to
 * badge and accent schools so their tier is identifiable at a glance.
 */

// Stored tier keys, entry → highest. A school row carries one of these.
// `on_demand` sits at the front because it is the entry tier with the
// smallest base footprint (everything beyond Dashboard + Resources is
// an explicit add-on).
export const TIER_ORDER = ['on_demand', 'bridge', 'lite', 'essentials', 'compliance', 'complete'];

export const SWISS_TIERS = [
  {
    key: 'on_demand',
    label: 'SWISS On-Demand',
    color: '#8a8073',
    blurb: 'Pay-per-use — the school accesses Dashboard and Resources by default; individual modules (Workshops, Sessions, Assessments, etc.) are switched on per school as needed.',
  },
  {
    key: 'bridge',
    label: 'SWISS Bridge',
    color: '#2f7376',
    blurb: 'The on-ramp — a 12-month counsellor-capacity programme that readies the school for SWISS certification.',
  },
  {
    key: 'lite',
    label: 'SWISS Lite',
    color: '#2f7376',
    blurb: 'Starter — monthly wellness touch, Gatekeeper certification and anonymous reporting.',
  },
  {
    key: 'essentials',
    label: 'SWISS Essentials',
    color: '#3f8f5e',
    blurb: 'The wellness standard — SWISS certification, Wellness Council and Quarterly Teacher Labs.',
  },
  {
    key: 'compliance',
    label: 'SWISS Compliance',
    color: '#8a4430',
    blurb: 'Everything in Essentials plus full POSH / POCSO compliance.',
  },
  {
    key: 'complete',
    label: 'SWISS Complete',
    color: '#ce7a0e',
    blurb: 'Full coverage — Compliance plus the annual physical safety audit suite.',
  },
];
const TIER_BY_KEY = Object.fromEntries(SWISS_TIERS.map((t) => [t.key, t]));

/** Map a legacy or loose tier value onto a canonical key. */
export function normalizeTierKey(t) {
  const v = String(t || '').trim().toLowerCase();
  if (TIER_ORDER.includes(v)) return v;
  // Legacy values from the old Standard/Premium/Enterprise scheme.
  if (v === 'standard') return 'lite';
  if (v === 'premium') return 'essentials';
  if (v === 'enterprise') return 'complete';
  // Loose / display forms ("SWISS Bridge", "Lite Plus", …).
  if (v.startsWith('bridge')) return 'bridge';
  if (v.startsWith('lite')) return 'lite';
  if (v.startsWith('essential')) return 'essentials';
  if (v.startsWith('compliance')) return 'compliance';
  if (v.startsWith('complete')) return 'complete';
  if (v === 'ondemand' || v === 'on-demand' || v === 'on demand'
      || v === 'ala_carte' || v === 'a-la-carte' || v === 'ala-carte'
      || v.startsWith('on-demand') || v.startsWith('on demand')) return 'on_demand';
  return 'essentials'; // sensible default for an unknown / unset tier
}

export function tierLabel(t) {
  return (TIER_BY_KEY[normalizeTierKey(t)] || {}).label || 'SWISS';
}
export function tierBlurb(t) {
  return (TIER_BY_KEY[normalizeTierKey(t)] || {}).blurb || '';
}
/** The tier's accent colour — used for badges and the school accent. */
export function tierColor(t) {
  return (TIER_BY_KEY[normalizeTierKey(t)] || {}).color || '#8a8073';
}

// The dashboard modules each tier unlocks. Explicit per tier — Bridge
// is a parallel programme, not a subset of Lite. On-Demand is the
// bare minimum (Dashboard + Resources): everything else has to be
// enabled per-school via addOnModules[].
const TIER_MODULES = {
  on_demand: ['dashboard', 'resources'],
  bridge: ['dashboard', 'anon', 'intellicare', 'workshops', 'resources'],
  lite: ['dashboard', 'teachers', 'sessions', 'anon', 'resources'],
  essentials: ['dashboard', 'teachers', 'sessions', 'anon', 'resources', 'progress', 'council', 'labs', 'workshops', 'intellicare'],
  compliance: ['dashboard', 'teachers', 'sessions', 'anon', 'resources', 'progress', 'council', 'labs', 'workshops', 'intellicare', 'compliance', 'posh', 'pocso'],
  complete: ['dashboard', 'teachers', 'sessions', 'anon', 'resources', 'progress', 'council', 'labs', 'workshops', 'intellicare', 'compliance', 'posh', 'pocso', 'audit'],
};

// POSH and POCSO ship inside the Compliance / Complete tiers, but an
// Essentials school can switch either on individually as a paid
// add-on. Two paths grant access: a legacy per-module boolean flag
// (kept for backward compat) AND the generic addOnModules[] array
// used by On-Demand schools. Either path enables the module.
const ADDON_MODULE_FLAG = {
  posh: 'poshEnabled',
  pocso: 'poscoEnabled',
};

/** The set of modules that can be enabled à la carte. Surfaced to the
 *  admin form as the multi-select picker — keep `dashboard` out (it's
 *  always on) and `resources` out (it's in every tier's base set). */
export const ADDON_MODULE_KEYS = [
  'teachers', 'progress', 'council', 'sessions', 'workshops', 'labs',
  'anon', 'intellicare', 'compliance', 'posh', 'pocso', 'audit',
  // SEMH Screening — schools license the SSWS instruments + the
  // counsellor's per-student trend view. Gated even on Complete-tier
  // schools so MSS controls who actually has the licence.
  'semh',
  // Learning Disabilities — schools license the dyslexia + attention
  // screeners. Same license-per-school pattern as SEMH.
  'ld',
  // SWISS WellTrack (ScholarCare) — licensed counselor caseload
  // module: student roster, EWQ wellbeing intake, session logs,
  // referrals, reports, crisis response. Same license-per-school
  // pattern as SEMH/LD — gated even on Complete so MSS controls the
  // grant given the clinical/crisis data involved.
  'scholarcare',
  // SWISS Safe Hands — staff onboarding and orientation (POCSO, POSH,
  // wellbeing and suicide prevention, bullying, data privacy, plus the
  // leadership role tracks) with completion records per staff member.
  // Add-on only for now, so MSS grants it per school.
  'safehands',
];

/**
 * Whether a school may access a module — its tier's modules plus any
 * per-school add-ons. Prefer this over moduleAllowed() wherever the
 * school record is in hand, so add-ons are honoured.
 */
export function schoolHasModule(school, moduleKey) {
  if (!moduleKey) return true;
  if (moduleAllowed(school?.tier, moduleKey)) return true;
  // Legacy boolean flag (poshEnabled / poscoEnabled / assessmentsEnabled).
  const flag = ADDON_MODULE_FLAG[moduleKey];
  if (flag && school && school[flag]) return true;
  // Generic add-on array — the On-Demand path, but usable by any tier
  // that wants a one-off module bump.
  if (Array.isArray(school?.addOnModules) && school.addOnModules.includes(moduleKey)) return true;
  return false;
}

/** The set of module keys a tier unlocks. */
export function tierModules(t) {
  return new Set(TIER_MODULES[normalizeTierKey(t)] || []);
}

/** Whether a tier may access a given module. Ungated keys are always allowed. */
export function moduleAllowed(tier, moduleKey) {
  if (!moduleKey) return true;
  return tierModules(tier).has(moduleKey);
}

/** True when no tier includes the module — it is only ever a per-school add-on. */
export function isAddonOnly(moduleKey) {
  return !!moduleKey && !TIER_ORDER.some((tk) => (TIER_MODULES[tk] || []).includes(moduleKey));
}

/** The lowest tier that unlocks a module — drives the "upgrade to X" copy. */
export function tierForModule(moduleKey) {
  for (const tk of TIER_ORDER) {
    if ((TIER_MODULES[tk] || []).includes(moduleKey)) return tk;
  }
  return 'complete';
}

// Human labels for each module — used by the locked screen + tooltips.
export const MODULE_LABEL = {
  dashboard: 'Dashboard',
  progress: 'Certification Progress',
  teachers: 'Teachers',
  council: 'Wellness Council',
  compliance: 'Compliance',
  posh: 'POSH Compliance',
  pocso: 'POCSO Compliance',
  anon: 'Anonymous Reports',
  intellicare: 'IntelliCare',
  labs: 'Teacher Labs',
  workshops: 'Workshops',
  sessions: 'In-person Sessions',
  resources: 'Resources',
  audit: 'Audit Suite',
  semh: 'SEMH Screening',
  ld: 'Learning Disabilities',
  scholarcare: 'SWISS WellTrack',
  safehands: 'SWISS Safe Hands',
};

// Route path (leading segment) → module key. Paths not listed here —
// e.g. /settings — are ungated and always reachable.
const MODULE_BY_PATH = {
  dashboard: 'dashboard',
  progress: 'progress',
  teachers: 'teachers',
  council: 'council',
  compliance: 'compliance',
  posh: 'posh',
  pocso: 'pocso',
  'anon-reports': 'anon',
  intellicare: 'intellicare',
  labs: 'labs',
  workshops: 'workshops',
  sessions: 'sessions',
  resources: 'resources',
  audit: 'audit',
  semh: 'semh',
  ld: 'ld',
  scholarcare: 'scholarcare',
  'safe-hands': 'safehands',
};

/** Resolve a pathname to its module key (or null when ungated). */
export function moduleForPath(pathname) {
  const seg = String(pathname || '').split('/').filter(Boolean)[0] || '';
  return MODULE_BY_PATH[seg] || null;
}
